// src/pages/Messages.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';

export default function Messages() {
  const { userId }   = useParams();       // /messages/:userId (optionnel)
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const [convs, setConvs]         = useState([]);
  const [messages, setMessages]   = useState([]);
  const [activeConv, setActiveConv] = useState(null); // { id, other_user_id, other_prenom }
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(true);
  const bottomRef = useRef(null);

  // Charger la liste des conversations
  useEffect(() => {
    api.get('/messages/conversations')
      .then(r => setConvs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Ouvrir une conversation via URL (/messages/:userId)
  useEffect(() => {
    if (userId) openConversation(parseInt(userId));
    // eslint-disable-next-line
  }, [userId]);

  const openConversation = useCallback(async (otherId, prenom) => {
    try {
      const res = await api.get(`/messages/${otherId}`);
      setActiveConv({ other_user_id: otherId, other_prenom: prenom || `Utilisateur ${otherId}`, conversation_id: res.data.conversation_id });
      setMessages(res.data.messages);
      // Mettre à jour les non-lus dans la liste
      setConvs(c => c.map(cv =>
        cv.other_user_id === otherId ? { ...cv, non_lus: 0 } : cv
      ));
    } catch (err) {
      console.error(err);
    }
  }, []);

  // Scroll en bas à chaque nouveau message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket – recevoir les messages en temps réel
  const handleWS = useCallback(payload => {
    if (payload.type === 'NEW_MESSAGE') {
      const msg = payload.data;
      if (activeConv && msg.sender_id !== user.id) {
        setMessages(m => [...m, msg]);
      }
      setConvs(c => {
        const exists = c.find(cv => cv.other_user_id === msg.sender_id);
        if (exists) {
          return c.map(cv => cv.other_user_id === msg.sender_id
            ? { ...cv, last_message: msg.contenu, last_message_at: msg.created_at, non_lus: activeConv?.other_user_id === msg.sender_id ? 0 : (cv.non_lus || 0) + 1 }
            : cv
          );
        }
        return c;
      });
    }
  }, [activeConv, user]);

  useWebSocket(handleWS);

  const sendMessage = async () => {
    if (!input.trim() || !activeConv) return;
    const text = input.trim();
    setInput('');
    try {
      const res = await api.post(`/messages/${activeConv.other_user_id}`, { contenu: text });
      setMessages(m => [...m, res.data]);
      setConvs(c => c.map(cv =>
        cv.other_user_id === activeConv.other_user_id
          ? { ...cv, last_message: text, last_message_at: new Date() }
          : cv
      ));
    } catch (err) {
      setInput(text);
    }
  };

  const SUGGESTIONS = ['Bonjour 🙂', 'Partant pour un café ?', 'Une marche cette semaine ?', 'Avec plaisir !'];

  return (
    <div style={{ paddingTop: 'var(--header-h)', paddingBottom: 'var(--botnav-h)', height: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <div style={s.layout}>

        {/* ── CONVERSATION LIST ─────────────────────────── */}
        <div style={{ ...s.convList, ...(activeConv ? s.convListHidden : {}) }}>
          <div style={s.convHeader}>Messages</div>
          {loading ? <div style={{ padding: 24 }}><div className="k-spinner" /></div>
          : convs.length === 0 ? (
            <div style={s.emptyConv}>
              <span className="material-symbols-outlined" style={{ fontSize: 36, color: 'var(--c-text-3)', marginBottom: 8 }}>chat_bubble_outline</span>
              <p>Aucune conversation pour l'instant.</p>
              <button style={{ ...s.newConvBtn }} onClick={() => navigate('/carte')}>
                Découvrir des personnes
              </button>
            </div>
          ) : convs.map(cv => (
            <div key={cv.id} style={{ ...s.convItem, ...(activeConv?.other_user_id === cv.other_user_id ? s.convItemActive : {}) }}
              onClick={() => openConversation(cv.other_user_id, cv.other_prenom)}>
              <div style={s.convAvatar}>{cv.other_prenom?.[0]?.toUpperCase()}</div>
              <div style={s.convInfo}>
                <div style={s.convName}>{cv.other_prenom}</div>
                <div style={s.convPreview}>{cv.last_message || '…'}</div>
              </div>
              <div style={{ flexShrink: 0 }}>
                {cv.non_lus > 0 && <div style={s.badge}>{cv.non_lus}</div>}
              </div>
            </div>
          ))}
        </div>

        {/* ── CHAT PANEL ────────────────────────────────── */}
        <div style={{ ...s.chatPanel, ...(activeConv ? s.chatPanelOpen : {}) }}>
          {!activeConv ? (
            <div style={s.noConv}>
              <span className="material-symbols-outlined" style={{ fontSize: 48, color: 'var(--c-text-3)', marginBottom: 12 }}>chat_bubble_outline</span>
              <p style={{ fontFamily: 'Manrope,sans-serif', color: 'var(--c-text-3)' }}>Sélectionnez une conversation</p>
            </div>
          ) : (
            <>
              <div style={s.chatHeader}>
                <button style={s.backBtn} onClick={() => setActiveConv(null)}>
                  <span className="material-symbols-outlined">arrow_back</span>
                </button>
                <div style={s.chatAvatar}>{activeConv.other_prenom[0].toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <div style={s.chatName}>{activeConv.other_prenom}</div>
                </div>
                <button style={s.profileBtn} onClick={() => navigate(`/profil/${activeConv.other_user_id}`)}>
                  Profil
                </button>
              </div>

              <div style={s.chatMessages}>
                <div style={s.msgHelper}>Commencez simplement. Un message suffit.</div>
                {messages.map(m => (
                  <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.sender_id === user.id ? 'flex-end' : 'flex-start' }}>
                    <div style={{ ...s.bubble, ...(m.sender_id === user.id ? s.bubbleSent : s.bubbleRecv) }}>
                      {m.contenu}
                    </div>
                    <div style={{ ...s.bubbleTime, ...(m.sender_id === user.id ? { textAlign: 'right' } : {}) }}>
                      {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <div style={s.suggestions}>
                {SUGGESTIONS.map(s2 => (
                  <button key={s2} style={s.suggestion} onClick={() => setInput(s2)}>{s2}</button>
                ))}
              </div>

              <div style={s.inputArea}>
                <input
                  style={s.chatInput}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  placeholder="Écrire un message…"
                />
                <button style={s.sendBtn} onClick={sendMessage}>
                  <span className="material-symbols-outlined" style={{ fontSize: 20 }}>send</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  layout:         { display: 'flex', flex: 1, overflow: 'hidden', height: '100%' },
  convList:       { width: '100%', borderRight: '1px solid var(--c-border)', overflowY: 'auto', background: 'var(--c-surface)', flexShrink: 0 },
  convListHidden: { display: 'none' }, // mobile seulement, géré via CSS responsive idéalement
  convHeader:     { padding: '20px 18px 12px', fontFamily: 'Manrope,sans-serif', fontSize: 20, fontWeight: 800, color: 'var(--c-primary)', borderBottom: '1px solid var(--c-border)', position: 'sticky', top: 0, background: 'var(--c-surface)', zIndex: 2 },
  convItem:       { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--c-border)', cursor: 'pointer', transition: 'background .15s' },
  convItemActive: { background: 'var(--c-primary-lt)' },
  convAvatar:     { width: 46, height: 46, borderRadius: '50%', background: 'var(--c-primary-lt)', color: 'var(--c-primary)', fontFamily: 'Manrope,sans-serif', fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  convInfo:       { flex: 1, minWidth: 0 },
  convName:       { fontFamily: 'Manrope,sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--c-text)', marginBottom: 2 },
  convPreview:    { fontSize: 13, color: 'var(--c-text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badge:          { background: 'var(--c-primary)', color: '#fff', fontFamily: 'Manrope,sans-serif', fontSize: 11, fontWeight: 700, width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  emptyConv:      { padding: 32, textAlign: 'center', fontFamily: 'Manrope,sans-serif', fontSize: 14, color: 'var(--c-text-3)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  newConvBtn:     { marginTop: 8, padding: '10px 18px', background: 'var(--c-primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius-pill)', fontFamily: 'Manrope,sans-serif', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  chatPanel:      { display: 'none', flexDirection: 'column', flex: 1, minWidth: 0, background: 'var(--c-bg)' },
  chatPanelOpen:  { display: 'flex' },
  noConv:         { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  chatHeader:     { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: 'var(--c-surface)', borderBottom: '1px solid var(--c-border)', flexShrink: 0 },
  backBtn:        { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary)', padding: 4, borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center' },
  chatAvatar:     { width: 38, height: 38, borderRadius: '50%', background: 'var(--c-primary-lt)', color: 'var(--c-primary)', fontFamily: 'Manrope,sans-serif', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  chatName:       { fontFamily: 'Manrope,sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--c-text)' },
  profileBtn:     { color: 'var(--c-primary)', background: 'var(--c-primary-lt)', border: 'none', borderRadius: 'var(--radius-pill)', padding: '6px 12px', fontFamily: 'Manrope,sans-serif', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  chatMessages:   { flex: 1, overflowY: 'auto', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 10 },
  msgHelper:      { textAlign: 'center', fontFamily: 'Manrope,sans-serif', fontSize: 12, color: 'var(--c-text-3)', paddingBottom: 4 },
  bubble:         { maxWidth: '75%', padding: '11px 14px', borderRadius: 18, fontSize: 14, lineHeight: 1.5 },
  bubbleRecv:     { background: 'var(--c-surface)', color: 'var(--c-text)', border: '1px solid var(--c-border)', borderBottomLeftRadius: 4 },
  bubbleSent:     { background: 'var(--c-primary)', color: '#fff', borderBottomRightRadius: 4 },
  bubbleTime:     { fontFamily: 'Manrope,sans-serif', fontSize: 10, color: 'var(--c-text-3)', marginTop: 2, padding: '0 2px' },
  suggestions:    { padding: '10px 12px 4px', display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none', flexShrink: 0 },
  suggestion:     { flexShrink: 0, padding: '8px 14px', borderRadius: 'var(--radius-pill)', background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', fontFamily: 'Manrope,sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--c-text-2)', cursor: 'pointer', whiteSpace: 'nowrap' },
  inputArea: {
  padding: '10px 14px calc(14px + env(safe-area-inset-bottom, 0px))',
  // ... rest
},
  chatInput:      { flex: 1, padding: '12px 16px', borderRadius: 'var(--radius-pill)', border: '1.5px solid var(--c-border)', background: 'var(--c-bg)', fontSize: 14, outline: 'none', color: 'var(--c-text)' },
  sendBtn:        { width: 42, height: 42, borderRadius: '50%', background: 'var(--c-primary)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
};
